import { lazy, Suspense, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Loader2, Users, Trophy, Settings as SettingsIcon } from "lucide-react";
import { useRightPanel, type PanelId } from "./useRightPanel";

// Page-style panels rendered inside a right-side Sheet. The existing
// page components own their own layout (Helmet, container, footer);
// dropping them inside the sheet content is acceptable for v1 and
// keeps a single source of truth — no logic duplication.
const SquadsPage = lazy(() => import("@/pages/Squads"));
const LeaderboardPage = lazy(() => import("@/pages/Leaderboard"));
const SettingsPage = lazy(() => import("@/pages/Settings"));

// Panels that already exist as floating sheets/dialogs listen to these
// window events. When the URL says `?panel=<id>` for one of them we
// fire the event and immediately strip the param — the sheet then
// owns its open state and its own close button.
const EVENT_PANELS: Partial<Record<PanelId, string>> = {
  wallet: "open-wallet",
  tutor: "open-tutor",
  memory: "open-memory",
  library: "open-library",
  bugs: "open-bug",
  hackathon: "open-hackathon",
};

const PAGE_PANELS: Partial<Record<PanelId, { title: string; Icon: typeof Users; Component: React.LazyExoticComponent<React.ComponentType> }>> = {
  squads: { title: "Squads", Icon: Users, Component: SquadsPage },
  leaders: { title: "Leaderboard", Icon: Trophy, Component: LeaderboardPage },
  settings: { title: "Settings", Icon: SettingsIcon, Component: SettingsPage },
};

/**
 * Mounted once at the app root. Reads `?panel=<id>` and:
 *   - For event panels (wallet/tutor/memory/library/bugs/hackathon),
 *     fires the corresponding `open-*` window event and clears the
 *     param. The existing sheet owns its lifecycle.
 *   - For page panels (squads/leaders/settings), renders the page
 *     component inside a right-side Sheet. Closing the sheet clears
 *     the param.
 */
export default function RightPanelHost() {
  const { activePanel, closePanel } = useRightPanel();

  // Bridge URL param -> legacy window-event sheets.
  useEffect(() => {
    if (!activePanel) return;
    const event = EVENT_PANELS[activePanel];
    if (!event) return;
    window.dispatchEvent(new Event(event));
    // Strip the param so the URL stays clean; the sheet now owns
    // its open state.
    closePanel();
  }, [activePanel, closePanel]);

  const page = activePanel ? PAGE_PANELS[activePanel] : null;
  const open = Boolean(page);

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) closePanel(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl lg:max-w-2xl p-0 flex flex-col"
      >
        {page && (
          <>
            <SheetHeader className="p-4 border-b border-border shrink-0">
              <SheetTitle className="font-mono text-sm flex items-center gap-2">
                <page.Icon className="w-4 h-4 text-primary" />
                {page.title}
              </SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto">
              <Suspense
                fallback={
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                }
              >
                <page.Component />
              </Suspense>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}